"use client";

import { JobPostingCardData, SchoolData } from "@nimble/candidate-api/types";
import { ChevronRight } from "lucide-react";
import { MouseEvent, useCallback } from "react";

import JobPostingBullets from "@/components/shared/jobs/job-posting-bullets";
import { schoolJobPostingsTestIds } from "@/lib/data-test-ids/schools.data-test-ids";
import preventNavigation from "@/lib/prevent-navigation";
import routes from "@/lib/routes";
import { StateConfig } from "@/lib/states-config";

interface SchoolPageJobRowViewProps {
  job: JobPostingCardData;
  schoolData: SchoolData;
  onJobClick: () => void;
  stateConfig: StateConfig;
}

export default function SchoolPageJobRowView({
  job,
  schoolData,
  onJobClick,
  stateConfig,
}: SchoolPageJobRowViewProps) {
  const jobUrl = routes.getSchoolJob({
    state: stateConfig,
    districtId: schoolData.district.nces_id,
    schoolId: schoolData.nces_id,
    jobId: job.uuid,
  });

  const handleClick = useCallback(
    (evt: MouseEvent<HTMLAnchorElement>) => {
      preventNavigation(evt);
      onJobClick();
    },
    [onJobClick],
  );

  return (
    <a
      href={jobUrl}
      data-testid={`${schoolJobPostingsTestIds.SCHOOL_JOB_ITEM}-${job.uuid}-${schoolData.nces_id}`}
      onClick={handleClick}
      className="group flex w-full items-center gap-3 py-4 transition-colors hover:bg-surface-subtle/80 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <div className="flex flex-col flex-1 min-w-0 justify-center gap-1">
        <p className="font-semibold text-base text-foreground group-hover:text-primary transition-colors line-clamp-2 break-words whitespace-normal">
          {job.title}
        </p>
        <JobPostingBullets job={job} className="text-xs text-ink-subtle">
          <JobPostingBullets.Category />
          <JobPostingBullets.EmploymentType />
          <JobPostingBullets.DatePosted />
          <JobPostingBullets.Tags />
        </JobPostingBullets>
      </div>
      <ChevronRight className="h-5 w-5 text-line-strong group-hover:text-ink-subtle transition-colors flex-shrink-0" />
    </a>
  );
}
